/**
 * @module PageErrorBoundary
 * @package @geenius/adapters/react
 * @description Shared page-level error boundary for React adapter pages.
 */

"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface PageErrorBoundaryProps {
  children: ReactNode;
  className?: string;
  title: string;
}

interface PageErrorBoundaryState {
  error: Error | null;
}

export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // React requires this hook for class error boundaries; callers handle logging.
  }

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className={cn("mx-auto max-w-3xl space-y-4", this.props.className)}>
        <div
          className="rounded-lg border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive shadow-sm"
          role="alert"
          aria-live="assertive"
        >
          <p className="font-semibold">{this.props.title}</p>
          <p className="mt-1">{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-4 rounded-md border border-destructive/30 bg-background px-4 py-2 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
}
